<script setup>
    import { computed, inject, toRef } from 'vue';
    import { Field, useField } from 'vee-validate';
    import { string } from 'yup';
    import { defaultFieldProps } from '../../helpers/defaultProps';
    import { useDccsField } from '../../composables/useDccsField';
    import { useI18n } from 'vue-i18n';

    const { t } = useI18n();

    const props = defineProps({
        ...defaultFieldProps,

        rows: {
            type: [Number, String],
            default: null,
        },

        cols: {
            type: [Number, String],
            default: null,
        },
        maxlength: {
            type: [Number, String],
            required: false,
            default: null,
        },
    });

    // eslint-disable-next-line no-unused-vars
    const emit = defineEmits(['update:modelValue']);

    const busy = inject('busy');

    const { fieldId, helpId, errorId, normalizedRequired, labelText, normalizedCols, normalizedRows, normalizedMaxlength } = useDccsField(props);
    const validationRules = computed(() => {
        let validator = string();

        if (normalizedRequired.value === true) {
            validator = validator.required(t('form.validation.required', { name: props.label }));
        }

        return validator;
    });

    const refName = toRef(props, 'name');
    const { errorMessage, value } = useField(refName, validationRules);
</script>

<template>
    <Field v-slot="{ field, errors }" v-model="value" as="div" :name="name" :label="label" class="dccs-field">
        <label v-if="label && label.length" :class="['dccs-label', { 'has-error': errors.length }]" :for="fieldId">
            {{ labelText }}
        </label>
        <div class="dccs-control">
            <textarea
                v-bind="field"
                :name="name"
                :id="fieldId"
                :class="['dccs-textarea', { 'has-error': errors.length }]"
                :placeholder="placeholder"
                :rows="normalizedRows"
                :cols="normalizedCols"
                :maxlength="normalizedMaxlength"
                :disabled="busy"
                :aria-required="normalizedRequired"
                :aria-invalid="errors.length > 0"
                :aria-describedby="
                    [message ? helpId : null, errors.length ? errorId : null].filter(Boolean).join(' ')
                "
            />
        </div>
        <p v-if="message && message.length" :id="helpId" class="dccs-help">
            {{ message }}
        </p>
        <p v-if="errors.length" :id="errorId" class="dccs-help is-danger" role="alert">
            {{ errorMessage || errors[0] }}
        </p>
    </Field>
</template>
